<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AnomalyDetectorSensorCard from '../Body/AnomalyDetectorSensorCard.vue'
import { useAnomalyDetectorStore } from '@/store/anomaly_detector'
import { ISensorGroup } from '@/interfaces/stores/anomalyDetector'
import { IStatsSensor } from '@/interfaces/entities/sensor'

const { t } = useI18n()

const store = useAnomalyDetectorStore()

const attentionDetails = ref(false)
const criticalDetails = ref(false)

const attentionSearch = ref('')
const criticalSearch = ref('')

const healthScore = computed(() => store.getTotalizers.health_score || 0)
const totalSensors = computed(() => store.getTotalizers.total_sensors || 0)
const attentionSensors = computed(() => store.getAttentionSensors)
const criticalSensors = computed(() => store.getCriticalSensors)

function groupBySensor(sensors: IStatsSensor[]): ISensorGroup[] {
    const map = new Map<number, ISensorGroup>()
    for (const s of sensors) {
        const id = s.sensor?.id ?? s.sensor_id
        if (!map.has(id)) {
            map.set(id, {
                sensorId: id,
                sensorName: s.sensor?.name ?? '-',
                fields: [],
            })
        }
        map.get(id)!.fields.push(s)
    }
    return Array.from(map.values())
}

const groupedAttention = computed(() => {
    const groups = groupBySensor(attentionSensors.value)
    const q = attentionSearch.value.trim().toLowerCase()
    if (!q) return groups
    return groups.filter((g) => g.sensorName.toLowerCase().includes(q))
})

const groupedCritical = computed(() => {
    const groups = groupBySensor(criticalSensors.value)
    const q = criticalSearch.value.trim().toLowerCase()
    if (!q) return groups
    return groups.filter((g) => g.sensorName.toLowerCase().includes(q))
})
</script>

<template>
    <div class="p-2 sm:flex-row sm:items-end lg:p-4">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-6 w-full">
            <div class="group relative bg-white px-3 py-8 shadow-sm border border-darkBlue-100 transition-all duration-300 hover:shadow-md">
                <div class="absolute top-0 left-0 h-full w-1 bg-darkBlue"></div>
                <div class="flex items-center justify-between mb-3">
                    <span class="text-xs font-bold text-slate-400 uppercase tracking-widest italic">{{ t('anomaly_detector.report_cards.analyzed_sensors') }}</span>
                    <div class="p-2 text-darkBlue"><i class="pi pi-microchip text-xs"></i></div>
                </div>
                <div>
                    <h3 class="text-3xl font-black text-slate-800 tracking-tight">{{ totalSensors }}</h3>
                    <p class="text-xs text-slate-500 mt-1 font-medium italic">{{ t('anomaly_detector.report_cards.sensors_in_operation') }}</p>
                </div>
            </div>

            <div class="group relative bg-white px-3 py-8 shadow-sm border border-darkBlue-100 transition-all duration-300 hover:shadow-md">
                <div class="absolute top-0 left-0 h-full w-1 bg-darkBlue"></div>
                <div class="flex items-center justify-between mb-3">
                    <span class="text-xs font-bold text-slate-400 uppercase tracking-widest italic">{{ t('anomaly_detector.report_cards.attention') }}</span>
                    <div class="p-2 text-yellow-500"><i class="pi pi-exclamation-triangle text-xs"></i></div>
                </div>
                <div>
                    <h3 class="text-3xl font-black text-slate-800 tracking-tight">{{ attentionSensors.length }}</h3>
                    <p class="text-xs text-slate-500 mt-1 font-medium italic">{{ t('anomaly_detector.report_cards.requires_observation') }}</p>
                </div>
                <button
                    v-if="attentionSensors.length > 0"
                    @click="attentionDetails = !attentionDetails"
                    class="absolute bottom-3 right-3 px-1 py-1 h-8 text-xs text-white bg-gray-400 hover:bg-gray-500"
                >
                    {{ t('anomaly_detector.report_cards.view_details') }}
                </button>
            </div>

            <div class="group relative bg-white px-3 py-8 shadow-sm border border-darkBlue-100 transition-all duration-300 hover:shadow-md">
                <div class="absolute top-0 left-0 h-full w-1 bg-darkBlue"></div>
                <div class="flex items-center justify-between mb-3">
                    <span class="text-xs font-bold text-slate-400 uppercase tracking-widest italic">{{ t('anomaly_detector.report_cards.critical') }}</span>
                    <div class="p-2 text-red-500"><i class="pi pi-exclamation-circle text-xs"></i></div>
                </div>
                <div>
                    <h3 class="text-3xl font-black text-slate-800 tracking-tight">{{ criticalSensors.length }}</h3>
                    <p class="text-xs text-slate-500 mt-1 font-medium italic">{{ t('anomaly_detector.report_cards.sensors_with_critical_variation') }}</p>
                </div>
                <button
                    v-if="criticalSensors.length > 0"
                    @click="criticalDetails = !criticalDetails"
                    class="absolute bottom-3 right-3 px-1 py-1 h-8 text-xs text-white bg-gray-400 hover:bg-gray-500"
                >
                    {{ t('anomaly_detector.report_cards.view_details') }}
                </button>
            </div>

            <div class="group relative bg-white px-3 py-8 shadow-sm border border-slate-100 transition-all duration-300 hover:shadow-md">
                <div :class="healthScore >= 50 ? 'bg-green-600' : 'bg-red-600'" class="absolute top-0 left-0 h-full w-1"></div>
                <div class="flex items-center justify-between mb-3">
                    <span class="text-xs font-bold text-slate-400 uppercase tracking-widest italic">{{ t('anomaly_detector.report_cards.health_score') }}</span>
                    <div :class="healthScore >= 50 ? 'text-green-500' : 'text-red-500'" class="p-2">
                        <i class="pi pi-wave-pulse text-xs"></i>
                    </div>
                </div>
                <div>
                    <div class="flex items-baseline space-x-1">
                        <h3 class="text-3xl font-black text-slate-800 tracking-tight">{{ healthScore }}</h3>
                        <span class="text-lg font-bold text-slate-400">%</span>
                    </div>
                    <p class="text-xs text-slate-500 mt-1 font-medium italic">{{ t('anomaly_detector.report_cards.sensors_with_normal_variation') }}</p>
                </div>
            </div>
        </div>
    </div>

    <transition
        enter-active-class="transition-all duration-300 ease-out"
        leave-active-class="transition-all duration-300 ease-in"
        enter-from-class="transform -translate-y-4 opacity-0"
        leave-to-class="transform -translate-y-1 opacity-0"
    >
        <div v-if="attentionDetails" class="relative mt-6 border-t border-stroke bg-white p-2 shadow-md sm:flex-row sm:items-end lg:p-4">
            <div class="flex items-center justify-between mb-3">
                <h2 class="text-xl font-bold border-l-2 border-orange-500 pl-2 text-gray-800">{{ t('anomaly_detector.report_cards.details_attention') }}</h2>
                
                <button @click="attentionDetails = false" class="bg-gray-400 hover:bg-gray-500 text-white py-1 px-2 text-sm">
                    {{ t('anomaly_detector.report_cards.hide') }}
                </button>
            </div>

            <p class="text-sm font-semibold text-gray-500 mb-3">{{ t('anomaly_detector.report_cards.click_card_for_details') }}</p>

            <div class="mb-3">
                <input
                    v-model="attentionSearch"
                    type="text"
                    :placeholder="t('anomaly_detector.report_cards.filter_by_sensor_name')"
                    class="w-full max-w-xs border border-gray-300 px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:border-orange-400"
                />
            </div>

            <div class="bg-gray-100 p-3 border border-stroke grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 py-2">
                <AnomalyDetectorSensorCard
                    v-for="group in groupedAttention"
                    :key="group.sensorId"
                    :group="group"
                    type="attention"
                />
                <p v-if="groupedAttention.length === 0" class="col-span-full text-sm text-gray-400 italic">
                    {{ t('anomaly_detector.report_cards.no_sensor_found_for', { query: attentionSearch }) }}
                </p>
            </div>
        </div>
    </transition>

    <transition
        enter-active-class="transition-all duration-300 ease-out"
        leave-active-class="transition-all duration-300 ease-in"
        enter-from-class="transform -translate-y-4 opacity-0"
        leave-to-class="transform -translate-y-1 opacity-0"
    >
        <div v-if="criticalDetails" class="relative mt-6 border-t border-stroke bg-white p-2 shadow-md sm:flex-row sm:items-end lg:p-4">
            <div class="flex items-center justify-between mb-3">
                <h2 class="text-xl font-bold border-l-2 border-red-500 pl-2 text-gray-800">{{ t('anomaly_detector.report_cards.details_critical') }}</h2>
                <button @click="criticalDetails = false" class="bg-gray-400 hover:bg-gray-500 text-white py-1 px-2 text-sm">
                    {{ t('anomaly_detector.report_cards.hide') }}
                </button>
            </div>
            <p class="text-sm font-semibold text-gray-500 mb-3">{{ t('anomaly_detector.report_cards.click_card_for_details') }}</p>

            <div class="mb-3">
                <input
                    v-model="criticalSearch"
                    type="text"
                    :placeholder="t('anomaly_detector.report_cards.filter_by_sensor_name')"
                    class="w-full max-w-xs border border-gray-300 px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:border-red-400"
                />
            </div>

            <div class="bg-gray-100 p-3 border border-stroke grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 py-2">
                <AnomalyDetectorSensorCard
                    v-for="group in groupedCritical"
                    :key="group.sensorId"
                    :group="group"
                    type="critical"
                />
                <p v-if="groupedCritical.length === 0" class="col-span-full text-sm text-gray-400 italic">
                    {{ t('anomaly_detector.report_cards.no_sensor_found_for', { query: criticalSearch }) }}
                </p>
            </div>
        </div>
    </transition>
</template>
